import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import Accordion from '../../../../components/Accordion/Accordion'
import Buttons from '../../../../components/ui/button/Buttons'
import { AccountInformationData, RepresentativeAuditlogConfig, RepresentativeAuditlogData } from './AccountInformation.data'
import FormCreator from '../../../../components/Forms/FormCreator'
import MolGrid from '../../../../components/Grid/MolGrid'
import SwalAlert from '../../../../services/swalService/SwalService'
import { useAddReinstatementToNewApplicationbyAdminMutation, useAdminSelectReinstatementMutation, useLazyGetAnalyticsInfomrationByCorporateMemberIdQuery, useLazyGetOptExpiryOrOptTerminationByIdQuery, useLazyGetReinstateMentButtonActiveQuery, useLazyGetSGBCCorporateRepresentativeLogByMemberIdQuery, useSaveOptExpiryOrOptTerminationByAdminMutation } from '../../../../app/services/corporateMembership/corporateMemberApi'
import { useParams } from 'react-router-dom'
import { decryptAESID } from '../../../../utils/Encryption'
import ToastService from '../../../../services/toastService/ToastService'
import CenterModel from '../../../../components/ui/centerModel/CenterModel'
import { handleConditionBaseFields, includesValueNormalized } from '../AccountInformation/Utils/AccountInformationUtils'

const PAGE_SIZE = 10;

const ACTION_TYPE_OPTIONS = [
  "Add", "Add Request", "Update", "Update Request", "Delete", "Delete Request",
].map((a) => ({ value: a, label: a }));

const EMPTY_FILTERS = { search: "", from: null, to: null, actionType: null };

const pad = (n) => String(n).padStart(2, "0");


const parseUtc = (value) => {
  if (!value) return null;
  const s = String(value);
  return new Date(s.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`);
};

const formatDateOnly = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const buildAuditForm = () => ({
  ...RepresentativeAuditlogData,
  formFields: RepresentativeAuditlogData.formFields.map((field) =>
    field.id === "actionType"
      ? { ...field, fieldSetting: { ...field.fieldSetting, options: ACTION_TYPE_OPTIONS } }
      : field
  ),
});

const AccountInformation = forwardRef((props, ref) => {
  const [formData, setFormData] = useState(AccountInformationData)
  const { id } = useParams();
  const editID = id ? decryptAESID(id, false) : 0
  const { error, success, confirm } = SwalAlert();
  const [isReinstateActive, setIsReinstateActive] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const AuditlogRef = useRef();
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [auditFormData, setAuditFormData] = useState(buildAuditForm);
  const [allLogs, setAllLogs] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [pageNo, setPageNo] = useState(1);

  const [adminSelectReinstatement, { data: adminSelectReinstatementData, isLoading: adminSelectReinstatementLoading, isFetching: adminSelectReinstatementFetching }] = useAdminSelectReinstatementMutation();
  const [getOptExpiryOrOptTerminationById, { data: optExpiryOrOptTerminationData, isLoading: optExpiryOrOptTerminationLoading, isSuccess: optExpiryOrOptTerminationSuccess }] = useLazyGetOptExpiryOrOptTerminationByIdQuery();
  const [saveOptExpiryOrOptTerminationByAdmin, { isSuccess: IsSuccess, isLoading: IsLoading, data: Data, isError: isError }] = useSaveOptExpiryOrOptTerminationByAdminMutation();
  const [getReinstateMentButtonActive, { data: reinstateMentButtonActiveData, isLoading: reinstateMentButtonActiveLoading, isSuccess: reinstateMentButtonActiveSuccess }] = useLazyGetReinstateMentButtonActiveQuery();
  const [addReinstatementToNewApplicationbyAdmin, { isSuccess: isAddReinstatementSuccess, isLoading: isAddReinstatementLoading, data: addReinstatementData, isError: isAddReinstatementError }] = useAddReinstatementToNewApplicationbyAdminMutation();
  const [getAnalyticsInformation, { data: getAnalyticsInformationResponse }] = useLazyGetAnalyticsInfomrationByCorporateMemberIdQuery();
  const [getRepresentativeLogs, { isFetching: logsIsFetching }] = useLazyGetSGBCCorporateRepresentativeLogByMemberIdQuery();

  const buttonClick = () => setShowModal(true);

  const loadLogs = async () => {
    try {
      const res = await getRepresentativeLogs(editID, false).unwrap();
      setAllLogs(Array.isArray(res) ? res : []);
    } catch {
      setAllLogs([]);
    }
  };

  useEffect(() => {
    if (isAuditModalOpen && editID > 0) {
      loadLogs();
    }
  }, [isAuditModalOpen]);

  const filteredLogs = useMemo(() => {
    const text = (appliedFilters.search || "").trim().toLowerCase();
    return allLogs.filter((log) => {
      const day = formatDateOnly(parseUtc(log.createdAt));
      if (text && !(log.message || "").toLowerCase().includes(text)) return false;
      if (appliedFilters.actionType && log.actionType !== appliedFilters.actionType) return false;
      if (appliedFilters.from && day < appliedFilters.from) return false;
      if (appliedFilters.to && day > appliedFilters.to) return false;
      return true;
    });
  }, [allLogs, appliedFilters]);

  const pagedLogs = useMemo(() => {
    const start = (pageNo - 1) * PAGE_SIZE;
    return filteredLogs.slice(start, start + PAGE_SIZE).map((item) => ({
      ...item,
      dateTime: parseUtc(item.createdAt)?.toISOString() ?? null,
    }));
  }, [filteredLogs, pageNo]);

  const openAuditModal = () => setIsAuditModalOpen(true);

  const closeAuditModal = () => {
    setIsAuditModalOpen(false);
    setAllLogs([]);
    setAppliedFilters(EMPTY_FILTERS);
    setAuditFormData(buildAuditForm());
    setPageNo(1);
  };

  const handleAuditSearch = () => {
    const f = AuditlogRef?.current?.getFormData();
    setAppliedFilters({
      search: f?.searchByDescription || "",
      from: formatDateOnly(f?.fromDate),
      to: formatDateOnly(f?.toDate),
      actionType: f?.actionType?.label || null,
    });
    setPageNo(1);
  };

  const handleAuditClear = () => {
    setAuditFormData((prev) => ({
      ...prev,
      initialState: { ...prev.initialState, searchByDescription: null, fromDate: null, toDate: null, actionType: null },
    }));
    setAppliedFilters(EMPTY_FILTERS);
    setPageNo(1);
  };

  const handleAuditPageChange = ({ pageNumber }) => setPageNo(pageNumber);

  const handleSubmit = () => {
    const formData = ref.current.getFormData();
    if (!formData) {
      error("Please fill in all mandatory fields to continue.", "");
      return;
    }
    let req = {
      IsOptOutExpiry: formData?.isOptOutExpiry,
      IsOptOutTermination: formData?.isOptOutTermination,
      SGBCCorporateMemberId: editID,
      MultiDomain: formData?.domainInfo
    }
    saveOptExpiryOrOptTerminationByAdmin(req);
  }

  const hadleReinstatementButton = () => {
    let req = {
      sGBCCorporateMemberId: editID
    }
    confirm("Confirm", "Confirm reinstatement?", "Yes", "Cancel").then(
      (value) => {
        if (value) {
          adminSelectReinstatement(req)
        }
      }
    );
  }

  const hadlenewApplicationButton = () => {
    let req = {
      memberId: editID
    }
    confirm("Confirm", "Confirm new application?", "Yes", "Cancel").then(
      (value) => {
        if (value) {
          addReinstatementToNewApplicationbyAdmin(req);
        }
      }
    );
  }

  useEffect(() => {
    if (id) {
      getOptExpiryOrOptTerminationById(editID);
      getReinstateMentButtonActive(editID);
      getAnalyticsInformation(editID)
    }
  }, [id])

  useEffect(() => {
    if (!adminSelectReinstatementLoading && adminSelectReinstatementData) {
      ToastService.success(addReinstatementData?.errorMessage.includes("Member is already allowed for reinstatement.") ? addReinstatementData?.errorMessage : "Member has been enabled for reinstatement.");
    }
  }, [adminSelectReinstatementLoading, adminSelectReinstatementData]);

  useEffect(() => {
    if (!isAddReinstatementLoading && addReinstatementData) {
      ToastService.success(addReinstatementData?.errorMessage);
    }
  }, [isAddReinstatementLoading, addReinstatementData]);

  useEffect(() => {
    if (!IsLoading && IsSuccess && Data) {
      ToastService.success("Your Request Submitted successfully.", "Successful");
    }
  }, [IsLoading, IsSuccess, Data]);

  useEffect(() => {
    if (!optExpiryOrOptTerminationLoading && optExpiryOrOptTerminationSuccess && optExpiryOrOptTerminationData) {
      let form = { ...formData };
      form = {
        ...form,
        initialState: {
          ...form.initialState,
          isOptOutExpiry: optExpiryOrOptTerminationData?.isOptOutExpiry,
          isOptOutTermination: optExpiryOrOptTerminationData?.isOptOutTermination,
          membershipStatus: optExpiryOrOptTerminationData?.subscriptionStatusId,
          membershipType: optExpiryOrOptTerminationData?.membershipTypeId,
          discontinuedReason: optExpiryOrOptTerminationData?.discontinueReasonId,
          OtherDiscontinuedReason: optExpiryOrOptTerminationData?.otherDiscontinuedReason,
          joinedDate: optExpiryOrOptTerminationData?.submissionDate,
          updatedDate: optExpiryOrOptTerminationData?.updatedAt,
          domainInfo: optExpiryOrOptTerminationData?.multiDomain
        },
      }
      setFormData(form);
    }
  }, [optExpiryOrOptTerminationLoading, optExpiryOrOptTerminationSuccess, optExpiryOrOptTerminationData]);

  useEffect(() => {
    if (!reinstateMentButtonActiveLoading && reinstateMentButtonActiveSuccess && reinstateMentButtonActiveData) {
      setIsReinstateActive(reinstateMentButtonActiveData?.isAllowToReinstatement);
    }
  }, [reinstateMentButtonActiveLoading, reinstateMentButtonActiveSuccess, reinstateMentButtonActiveData]);

  useEffect(() => {
    const selectedValue = formData?.initialState?.discontinuedReason;

    if (selectedValue !== undefined) {
      let updated = handleConditionBaseFields(
        selectedValue,
        formData,
        "OtherDiscontinuedReason",
        10
      );

      if (!includesValueNormalized(selectedValue, 10)) {
        updated = {
          ...updated,
          initialState: {
            ...updated.initialState,
            OtherReasonOfJoining: null,
          },
        };
      }

      setFormData(updated);
    }
  }, [formData?.initialState?.discontinuedReason]);

  return (
    <>
      <Accordion title="Account Information" accordion={false} buttonLabel="Analytics" clickEvent={buttonClick} headerActions={ <Buttons buttonText="Membership Audit Log" buttonTypeClassName="outline-btn" onClick={openAuditModal} />}>
        <div className="row">
          <FormCreator ref={ref} {...formData} />
        </div>
        <div className="btn-right-side">
          <Buttons
            buttonText="Save"
            buttonTypeClassName="success-btn"
            onClick={handleSubmit}
            isLoading={IsLoading}
            isDisable={props?.roleId === 12}
          />
          <Buttons
            buttonText="Reinstate"
            buttonTypeClassName="outline-btn"
            onClick={hadleReinstatementButton}
            isDisable={!isReinstateActive || props?.roleId === 12}
          />
          <Buttons
            buttonText="New Application"
            buttonTypeClassName="success-btn"
            onClick={hadlenewApplicationButton}
            isDisable={!isReinstateActive || props?.roleId === 12}
          />    
        </div>
      </Accordion>

      <CenterModel
        show={showModal}
        handleClose={() => setShowModal(false)}
        modelTitle=""
      >
        <div className="analytics-report">
          <h4 className="title">Analytics for the current year</h4>
          <div className="analytics-box">
            <table className="analytics-table">
              <tbody className="analytics-table-body">
                <tr >
                  <td>Number of Active SGBP Certificates</td>
                  <td className="value">0</td>
                </tr>
                <tr>
                  <td>Number of Active SGBS Certificates</td>
                  <td className="value">0</td>
                </tr>
                <tr>
                  <td>Number of Events attended</td>
                  <td className="value">{getAnalyticsInformationResponse?.eventAttendanceCount ?? 0}</td>
                </tr>
                <tr>
                  <td>Number of GMPs in Organisation</td>
                  <td className="value">{getAnalyticsInformationResponse?.organizationGmpCount ?? 0}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </CenterModel>

      {isAuditModalOpen && (
        <CenterModel modelTitle={"Membership Management Audit Log"} show={isAuditModalOpen} handleClose={closeAuditModal}>
          <div className="crud-requirement-list">
            <div className="corporate-member-list">
              <div className="sgbp-category">
                <div className="row w-100">
                  <div className="col-10 row">
                    <FormCreator ref={AuditlogRef} {...auditFormData} />
                  </div>
                  <div className="col-2 audit-btn-group">
                    <Buttons buttonText="Search" buttonTypeClassName="search-btn " onClick={handleAuditSearch} />
                    <Buttons buttonText="Clear" buttonTypeClassName="btn btn-secondary" onClick={handleAuditClear} />
                  </div>
                </div>
              </div>

              <div className="audit-log-list model-audit-log-list">
                <MolGrid
                  configuration={RepresentativeAuditlogConfig}
                  dataSource={pagedLogs}
                  isLoading={logsIsFetching}
                  allowPagination={true}
                  pagination={{ pageNo, pageSize: PAGE_SIZE, totalCount: filteredLogs.length }}
                  onPageChange={handleAuditPageChange}
                />
              </div>
            </div>
          </div>
        </CenterModel>
      )}
    </>
  )
})

export default AccountInformation
